import { Input, InputRef, Typography } from "antd";
import isEmpty from "lodash/isEmpty";
import React, { ReactElement, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import MediaIcon from "../../assets/icons/media-chat.svg";
import MessageIcon from "../../assets/icons/script-chat.svg";
import MicroIcon from "../../assets/icons/audio-chat.svg";
import "./index.less";

type props = {
  onSendClick?: (value: string) => void;
  onMediaClick?: () => void;
  onChatScriptClick?: () => void;
  onAudioClick?: () => void;
  quickAnswer?: string;
  hideScripts?: boolean; // Add prop to hide scripts button for fan side
  hideAudio?: boolean; // Add prop to hide audio button for fan side
};

const { TextArea } = Input;

const MessageInputComponent: React.FC<props> = ({
  onSendClick,
  onMediaClick,
  onChatScriptClick,
  onAudioClick,
  quickAnswer,
  hideScripts = false,
  hideAudio = false,
}) => {
  const { t } = useTranslation();
  const [suffix, setSuffix] = useState<ReactElement | null>(null);
  const [value, setValue] = useState<string>("");
  const [showPrefix, setShowPrefix] = useState<boolean>(true);
  const [resetKey, setResetKey] = useState<number>(0);
  const [shouldFocus, setShouldFocus] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const inputRef = React.useRef<InputRef>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const handleOnClick = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      const trimmedValue = value.replace(/^[ \t]+|[ \t]+$/g, "");
      if (onSendClick && trimmedValue) {
        onSendClick(trimmedValue);
        setValue("");
        setResetKey((prev) => prev + 1);
        setShouldFocus(true);
      }
    },
    [value, onSendClick]
  );

  useEffect(() => {
    if (shouldFocus && inputRef.current) {
      inputRef.current.focus();
      setShouldFocus(false);
    }
  }, [resetKey, shouldFocus]);

  const handleMediaClick = useCallback(
    (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      e.preventDefault();
      e.stopPropagation();
      if (onMediaClick) {
        onMediaClick();
      }
    },
    [onMediaClick]
  );

  const handleChatScriptClick = useCallback(
    (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      e.preventDefault();
      e.stopPropagation();
      if (onChatScriptClick) {
        onChatScriptClick();
      }
    },
    [onChatScriptClick]
  );

  const handleAudioClick = useCallback(
    (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      e.preventDefault();
      e.stopPropagation();
      if (onAudioClick) onAudioClick();
    },
    [onAudioClick]
  );

  useEffect(() => {
    if (quickAnswer && !isEmpty(quickAnswer)) {
      setValue(quickAnswer);
    }
  }, [quickAnswer]);

  useEffect(() => {
    let newSuffix = null;
    if (!isEmpty(value)) {
      newSuffix = (
        <Typography.Text key="sendBtn" className="messageSendLabel">
          {t("send")}
        </Typography.Text>
      );
    }
    setSuffix(newSuffix);
    if (isEmpty(value) !== showPrefix) {
      setShowPrefix(isEmpty(value));
    }
  }, [value, t, handleOnClick]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isMobile && e.key === "Enter") {
        if (e.shiftKey) {
          return;
        }

        e.preventDefault();
        const trimmedValue = value.replace(/^[ \t]+|[ \t]+$/g, "");
        if (onSendClick && trimmedValue) {
          onSendClick(trimmedValue);
          setValue("");
          setResetKey((prev) => prev + 1);
          setShouldFocus(true);
        }
      }
    },
    [isMobile, value, onSendClick]
  );

  return (
    <>
      <div
        className={`message-input-row-inline ${hideScripts && hideAudio ? "single-icon" : hideScripts || hideAudio ? "two-icons" : "three-icons"} ${!showPrefix ? "no-icons" : ""}`}
      >
        {showPrefix && (
          <div className="footer-icons">
            {!hideScripts && (
              <button className="icon-button" onClick={handleChatScriptClick}>
                <img src={MessageIcon} alt="Quick message" className="footer-icon" />
              </button>
            )}
            <button className="icon-button" onClick={handleMediaClick}>
              <img src={MediaIcon} alt="Media" className="footer-icon" />
            </button>
            {!hideAudio && (
              <button className="icon-button" onClick={handleAudioClick}>
                <img src={MicroIcon} alt="Record Audio" className="footer-icon" />
              </button>
            )}
          </div>
        )}

        <div className="message-input-container">
          <TextArea
            key={resetKey}
            ref={inputRef}
            className={`messageInput ${!isEmpty(value) ? "has-suffix" : ""}`}
            placeholder={t("your-message")!}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoSize={{ minRows: 1, maxRows: 6 }}
          />
          {!isEmpty(value) && (
            <div className="message-input-suffix" onClick={handleOnClick}>
              {suffix}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MessageInputComponent;
